const STORAGE_KEY = 'fechaCNAE';
const EMPTY = 'EMPTY';
const CUSTOM_EVENT_NAME = 'fechaCNAEChanged';

/**
 * Convierte el valor raw de localStorage a una fecha usable.
 * Centraliza el manejo del centinela "EMPTY".
 */
export const resolveFechaCNAE = (value: string | null): string =>
  (!value || value === EMPTY) ? '' : value;

/**
 * Lee fechaCNAE de localStorage y resuelve el centinela.
 */
export const getFechaCNAE = (): string =>
  resolveFechaCNAE(localStorage.getItem(STORAGE_KEY));

/**
 * Lee el valor raw de localStorage (sin resolver el centinela).
 * Retorna null si la clave no existe.
 */
export const getRawFechaCNAE = (): string | null =>
  localStorage.getItem(STORAGE_KEY);

/**
 * Escribe fechaCNAE en localStorage y dispara el custom event.
 * Si el valor es vacío, guarda el centinela "EMPTY".
 */
export const setFechaCNAE = (fecha: string): void => {
  const value = fecha || EMPTY;
  localStorage.setItem(STORAGE_KEY, value);
  window.dispatchEvent(new CustomEvent(CUSTOM_EVENT_NAME, { detail: value }));
};

/**
 * Elimina fechaCNAE de localStorage y dispara el custom event.
 */
export const clearFechaCNAE = (): void => {
  localStorage.removeItem(STORAGE_KEY);
  window.dispatchEvent(new CustomEvent(CUSTOM_EVENT_NAME, { detail: '' }));
};

/**
 * Observa cambios en fechaCNAE usando StorageEvent (entre pestanas)
 * y CustomEvent (misma pestana). Retorna una funcion de cleanup.
 */
export const watchFechaCNAE = (onChange: (fecha: string) => void): (() => void) => {
  let last = localStorage.getItem(STORAGE_KEY) || '';
  onChange(resolveFechaCNAE(last));

  const handleStorage = (e: StorageEvent) => {
    if (e.key === STORAGE_KEY) {
      const current = e.newValue || '';
      if (current !== last) {
        last = current;
        onChange(resolveFechaCNAE(current));
      }
    }
  };

  const handleCustom = (ev: Event) => {
    const current = (ev as CustomEvent).detail || localStorage.getItem(STORAGE_KEY) || '';
    if (current !== last) {
      last = current;
      onChange(resolveFechaCNAE(current));
    }
  };

  window.addEventListener('storage', handleStorage);
  window.addEventListener(CUSTOM_EVENT_NAME, handleCustom);

  return () => {
    window.removeEventListener('storage', handleStorage);
    window.removeEventListener(CUSTOM_EVENT_NAME, handleCustom);
  };
};
